import {
  type AdminAgentModelProfileId,
  type AdminAgentModelSettings,
  type AdminBillingView,
  type AdminEmailRoutingSettings,
  type AdminFeatureFlag,
  type AdminFeatureFlags,
  type AdminInvoiceCardRecipientPublic,
  type AdminJurisdiction,
  type AdminJurisdictionSettings,
  type AdminN1TemplateStatus,
  type AdminN1TemplateUploadResponse,
  type AdminN4TemplateStatus,
  type AdminN4TemplateUploadResponse,
  type AdminOperationalConfigView,
  type AdminPmBindingPublic,
  type AdminPmGroupBindingPublic,
  type AdminPmGroupMembershipPublic,
  type AdminPmInviteCreateResponse,
  type AdminSetupInviteCreateResponse,
  type AdminSetupState,
  type AdminSiteRecoverySettings,
  ApiError,
  ApiNetworkError,
  type ClientStatus,
  type ClientTenantPublic,
  type PmGroupKind,
  type UserRole,
  adminAgentModelGet,
  adminAgentModelSet,
  adminAgentModelTest,
  adminBillingAgreementUpload,
  adminBillingGet,
  adminBillingPaymentCreate,
  adminBillingSet,
  adminClientsGet,
  adminEmailRoutingGet,
  adminEmailRoutingSet,
  adminFeatureFlagsGet,
  adminFeatureFlagsSet,
  adminInvoiceCardRecipientSet,
  adminInvoiceCardRecipientsList,
  adminJurisdictionGet,
  adminJurisdictionSet,
  adminN1TemplateDelete,
  adminN1TemplateStatus,
  adminN1TemplateUpload,
  adminN4TemplateDelete,
  adminN4TemplateStatus,
  adminN4TemplateUpload,
  adminOperationalConfigGet,
  adminOperationalConfigSet,
  adminPmBindingsList,
  adminPmGroupMembershipSet,
  adminPmGroupsGet,
  adminPmInviteCreate,
  adminSetupInviteCreate,
  adminSetupState,
  adminSiteRecoveryGet,
  adminSiteRecoverySet,
} from '@cortex/web-api/clients/admin/api';
import { FEATURE_FLAG_META } from '@cortex/web-api/clients/admin/feature-flags';
import { Wordmark } from '@cortex/web-api/ui/admin/Wordmark';
import { useEffect, useRef, useState } from 'react';
import { ManagedEmailOnboardingPanel } from '../components/ManagedEmailOnboardingPanel.js';

/**
 * v1.2 PM-onboarding (PR1) — drilldown from a ClientsScreen card.
 *
 * Layout per plan section H:
 *   - Header: name, slug, status, public URL
 *   - Setup status panel (PR1 stubs every row "awaiting" — PR2/3 wire real
 *     state from /pm/setup/state on the tenant subdomain)
 *   - PMs section (PR1 placeholder — the platform pm_users table lands in PR4)
 *   - Operations (PR1 wires nothing; buttons disabled with PR-N labels)
 *
 * Why everything is a stub right now: PR1's job is the foundation (schema +
 * cookie + invite/start endpoints + this screen). Lighting the rows up needs
 * the cards in PR2/3/5 to actually write tenant_settings + tenant_pm_users.
 */

interface Props {
  slug: string;
  onSessionExpired: () => void;
  onBack: () => void;
  /** PR7 — admin clicks a bound PM row to drill into their chat thread. */
  onViewPmConversation: (chatId: string, groupKind?: PmGroupKind | null) => void;
}

export function TenantDetailScreen({
  slug,
  onSessionExpired,
  onBack,
  onViewPmConversation,
}: Props) {
  const [client, setClient] = useState<ClientTenantPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await adminClientsGet(slug);
      setClient(res.client);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setError('No client with that slug.');
      } else if (err instanceof ApiNetworkError) {
        setError("Couldn't reach the server.");
      } else {
        setError('Could not load tenant.');
      }
    } finally {
      setLoading(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is stable; intentional one-shot per slug
  useEffect(() => {
    void load();
  }, [slug]);

  return (
    <div className="app">
      <header className="app-header" style={{ justifyContent: 'space-between', display: 'flex' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <img src="/acel-cortex-mark.svg" alt="" width={32} height={32} />
          <Wordmark />
        </div>
        <button
          type="button"
          onClick={onBack}
          className="btn-secondary"
          style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
        >
          ← clients
        </button>
      </header>
      <main
        className="app-main"
        style={{ overflow: 'auto', padding: 'var(--space-8) var(--space-6)' }}
      >
        <div
          style={{
            maxWidth: 960,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-6)',
          }}
        >
          {loading && client === null ? (
            <p className="field-help">loading…</p>
          ) : error && client === null ? (
            <div className="card" style={{ padding: 'var(--space-6)' }}>
              <p className="field-error">{error}</p>
              <button
                type="button"
                onClick={load}
                className="btn-secondary"
                style={{ marginTop: 'var(--space-4)' }}
              >
                retry
              </button>
            </div>
          ) : client ? (
            <>
              <TenantHeader client={client} />
              <ManagedEmailOnboardingPanel
                key={client.slug}
                slug={client.slug}
                onSessionExpired={onSessionExpired}
              />
              <SetupStatusPanel tenantSlug={client.slug} onSessionExpired={onSessionExpired} />
              <EmailRoutingPanel tenantSlug={client.slug} onSessionExpired={onSessionExpired} />
              <SiteRecoveryPanel tenantSlug={client.slug} onSessionExpired={onSessionExpired} />
              <FeatureFlagsPanel tenantSlug={client.slug} onSessionExpired={onSessionExpired} />
              <JurisdictionPanel tenantSlug={client.slug} onSessionExpired={onSessionExpired} />
              <OperationalConfigPanel
                tenantSlug={client.slug}
                onSessionExpired={onSessionExpired}
              />
              <BillingAdminPanel tenantSlug={client.slug} onSessionExpired={onSessionExpired} />
              <AgentModelPanel tenantSlug={client.slug} onSessionExpired={onSessionExpired} />
              <PmsSection
                tenantSlug={client.slug}
                tenantStatus={client.status}
                onSessionExpired={onSessionExpired}
                onViewPmConversation={onViewPmConversation}
              />
              <SetupInviteSection
                tenantSlug={client.slug}
                tenantStatus={client.status}
                onSessionExpired={onSessionExpired}
              />
              <TemplateSection
                formKind="n4"
                tenantSlug={client.slug}
                tenantStatus={client.status}
                onSessionExpired={onSessionExpired}
                api={{
                  status: adminN4TemplateStatus,
                  upload: adminN4TemplateUpload,
                  delete: adminN4TemplateDelete,
                }}
              />
              <TemplateSection
                formKind="n1"
                tenantSlug={client.slug}
                tenantStatus={client.status}
                onSessionExpired={onSessionExpired}
                api={{
                  status: adminN1TemplateStatus,
                  upload: adminN1TemplateUpload,
                  delete: adminN1TemplateDelete,
                }}
              />
              <OperationsSection />
            </>
          ) : null}
        </div>
      </main>
      <footer className="app-footer">acel·cortex internal</footer>
    </div>
  );
}

function TenantHeader({ client }: { client: ClientTenantPublic }) {
  const subdomain = `${client.slug}.acelcortex.ai`;
  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          <h1 className="h2" style={{ color: 'var(--ink)' }}>
            {client.displayName}
          </h1>
          <a
            href={`https://${subdomain}`}
            target="_blank"
            rel="noreferrer"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              letterSpacing: 'var(--tracking-mono)',
              color: 'var(--muted)',
            }}
          >
            {subdomain}
          </a>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              letterSpacing: 'var(--tracking-mono)',
              color: 'var(--muted)',
              textTransform: 'uppercase',
            }}
          >
            PMS: {client.pmsProvider}
          </span>
          {client.onboarding && (
            <p className="field-help">
              Sales intake: {client.onboarding.contactName} · {client.onboarding.contactEmail}
              <br />
              Ping notification: {client.onboarding.notification}
              {client.onboarding.notifiedAt ? ` · ${client.onboarding.notifiedAt}` : ''}
            </p>
          )}
        </div>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-mono-sm)',
            letterSpacing: 'var(--tracking-mono)',
            textTransform: 'uppercase',
            color: statusColor(client.status),
          }}
        >
          {client.status}
        </span>
      </div>
    </div>
  );
}

function SetupStatusPanel({
  tenantSlug,
  onSessionExpired,
}: {
  tenantSlug: string;
  onSessionExpired: () => void;
}) {
  const [state, setState] = useState<AdminSetupState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onSessionExpired is stable; effect closes over tenantSlug
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminSetupState(tenantSlug)
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        setError(
          err instanceof ApiNetworkError
            ? "Couldn't reach the server."
            : err instanceof ApiError && err.status === 502
              ? "Couldn't reach the tenant container."
              : "Couldn't load setup state.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-4)' }}>
        setup status
      </h2>
      {loading && state === null && <p className="field-help">loading…</p>}
      {!loading && error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {state?.settingsKeyMissing && (
        <p className="field-error" role="alert" style={{ marginBottom: 'var(--space-3)' }}>
          tenant container is missing SETTINGS_MASTER_KEY — every row will report awaiting
          regardless of what the PM has actually connected.
        </p>
      )}
      {state && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <SetupRow
            label={state.pms?.label ?? 'Buildium'}
            status={
              (state.pms?.connection.connected ??
              state.cards.pms?.connected ??
              state.cards.buildium.connected)
                ? 'done'
                : 'awaiting'
            }
          />
          {state.pms && (
            <p className="field-help" style={{ marginTop: 'calc(var(--space-2) * -1)' }}>
              status: {state.pms.connection.status} · capabilities:{' '}
              {state.pms.connection.enabledCapabilities.join(', ') || 'none enabled'} · verified
              capabilities: {state.pms.connection.verifiedCapabilities.join(', ') || 'none'} · last
              verified: {state.pms.connection.lastVerifiedAt ?? 'never'} · sync:{' '}
              {state.pms.connection.lastSyncAt ?? 'never'}
              {state.pms.connection.safeError ? ` · ${state.pms.connection.safeError}` : ''}
            </p>
          )}
          <SetupRow
            label="Email forwarding"
            status={state.cards.email.connected ? 'done' : 'awaiting'}
          />
          <SetupRow
            label="Calendar"
            status={state.cards.calendar.connected ? 'done' : 'awaiting'}
          />
          <SetupRow label="Drive" status={state.cards.drive.connected ? 'done' : 'awaiting'} />
          <SetupRow
            label="PM dashboard login"
            status={state.cards.phone.connected ? 'done' : 'awaiting'}
          />
        </div>
      )}
    </div>
  );
}

function EmailRoutingPanel({
  tenantSlug,
  onSessionExpired,
}: {
  tenantSlug: string;
  onSessionExpired: () => void;
}) {
  const [settings, setSettings] = useState<AdminEmailRoutingSettings | null>(null);
  const [forwardTo, setForwardTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onSessionExpired is stable; effect closes over tenantSlug
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotice(null);
    adminEmailRoutingGet(tenantSlug)
      .then((next) => {
        if (!cancelled) {
          setSettings(next);
          setForwardTo(next.forwardTo ?? '');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        setError(
          err instanceof ApiNetworkError
            ? "Couldn't reach the server."
            : err instanceof ApiError && err.status === 502
              ? "Couldn't reach the tenant container."
              : "Couldn't load email routing.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  async function save() {
    const submitted = forwardTo;
    const value = submitted.trim();
    if (!value) {
      setError('Enter the inbox that should receive fallback emails.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const next = await adminEmailRoutingSet(tenantSlug, { forwardTo: value });
      setSettings(next);
      setForwardTo((current) => (current === submitted ? (next.forwardTo ?? value) : current));
      setNotice('Email forwarding updated. Cloudflare and tenant settings are in sync.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      setError(emailRoutingErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-2)' }}>
        email routing
      </h2>
      <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
        Controls manual/fallback emails sent directly to the tenant mailbox. Tracked prepare-email
        replies still go to Cortex and alert the original Telegram chat.
      </p>
      {loading && settings === null && <p className="field-help">loading…</p>}
      {!loading && error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <output className="field-help" style={{ color: 'var(--green)' }}>
          {notice}
        </output>
      )}
      {settings && (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <span className="field-label">fallback address</span>
            <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>
              {settings.address}
            </code>
            {settings.deprecatedAddresses.length > 0 && (
              <p className="field-help">
                Deprecated aliases no longer shown to users:{' '}
                {settings.deprecatedAddresses.join(', ')}
              </p>
            )}
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <span className="field-label">tracked reply example</span>
            <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>
              {settings.trackedReplyExample}
            </code>
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <label className="field-label" htmlFor={`email-forward-to-${tenantSlug}`}>
              forward fallback emails to
            </label>
            <input
              id={`email-forward-to-${tenantSlug}`}
              type="email"
              value={forwardTo}
              onChange={(e) => setForwardTo(e.currentTarget.value)}
              placeholder="pm@company.com"
              autoComplete="email"
            />
            <p className="field-help">
              Cloudflare requires this destination to be verified before the save can succeed.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn-secondary"
              disabled={saving || forwardTo.trim() === (settings.forwardTo ?? '')}
              onClick={save}
              style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
            >
              {saving ? 'saving…' : 'save email routing'}
            </button>
            <span className="field-help">
              {settings.configured ? `current: ${settings.forwardTo}` : 'not configured'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function SiteRecoveryPanel({
  tenantSlug,
  onSessionExpired,
}: {
  tenantSlug: string;
  onSessionExpired: () => void;
}) {
  const [settings, setSettings] = useState<AdminSiteRecoverySettings | null>(null);
  const [siteRecoveryEmail, setSiteRecoveryEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onSessionExpired is stable; effect closes over tenantSlug
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotice(null);
    adminSiteRecoveryGet(tenantSlug)
      .then((next) => {
        if (!cancelled) {
          setSettings(next);
          setSiteRecoveryEmail(next.siteRecoveryEmail ?? '');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        setError(
          err instanceof ApiNetworkError
            ? "Couldn't reach the server."
            : err instanceof ApiError && err.status === 502
              ? "Couldn't reach the tenant container."
              : "Couldn't load site recovery settings.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  async function saveValue(value: string | null) {
    const submitted = siteRecoveryEmail;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const next = await adminSiteRecoverySet(tenantSlug, { siteRecoveryEmail: value });
      setSettings(next);
      setSiteRecoveryEmail((current) =>
        current === submitted ? (next.siteRecoveryEmail ?? '') : current,
      );
      setNotice(
        next.siteRecoveryEmail ? 'Site recovery email updated.' : 'Site recovery email cleared.',
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      setError(siteRecoveryErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const trimmed = siteRecoveryEmail.trim();
  const current = settings?.siteRecoveryEmail ?? '';
  const dirty = trimmed !== current;

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-2)' }}>
        site login recovery email
      </h2>
      <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
        Receives shared-login password-reset links for this tenant site.
      </p>
      {loading && settings === null && <p className="field-help">loading…</p>}
      {!loading && error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <output className="field-help" style={{ color: 'var(--green)' }}>
          {notice}
        </output>
      )}
      {settings && (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <label className="field-label" htmlFor={`site-recovery-email-${tenantSlug}`}>
              Site login recovery email
            </label>
            <input
              id={`site-recovery-email-${tenantSlug}`}
              type="email"
              value={siteRecoveryEmail}
              onChange={(e) => setSiteRecoveryEmail(e.currentTarget.value)}
              placeholder="ops@company.com"
              autoComplete="email"
            />
            <p className="field-help">{siteRecoveryStatusText(settings)}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn-secondary"
              disabled={saving || !dirty}
              onClick={() => saveValue(trimmed || null)}
              style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
            >
              {saving ? 'saving…' : 'save recovery email'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={saving || (!settings.siteRecoveryEmail && !trimmed)}
              onClick={() => saveValue(null)}
              aria-label="clear recovery email"
              style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
            >
              clear
            </button>
            <span className="field-help">
              {settings.siteRecoveryEmail
                ? `current: ${settings.siteRecoveryEmail}`
                : 'not configured'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function siteRecoveryStatusText(settings: AdminSiteRecoverySettings): string {
  if (settings.siteRecoveryEmail) {
    return `Password reset links are sent to ${settings.siteRecoveryEmail}.`;
  }
  if (settings.cortexEmailForwardTo) {
    return `Currently falling back to ${settings.cortexEmailForwardTo}.`;
  }
  return 'No recovery email — password reset is disabled for this tenant.';
}

function siteRecoveryErrorMessage(err: unknown): string {
  if (err instanceof ApiNetworkError) return "Couldn't reach the server.";
  if (err instanceof ApiError && err.status === 502) return "Couldn't reach the tenant container.";
  if (err instanceof ApiError && err.status === 400) return 'Enter a valid recovery email.';
  return "Couldn't save site recovery settings.";
}

/**
 * v1.3 per-tenant feature toggles. Reads tenant_settings booleans via the
 * admin forward proxy and PATCHes one flag at a time. Toggles take effect on
 * the next agent turn (getSettings is read fresh per turn) — no restart.
 */
function FeatureFlagsPanel({
  tenantSlug,
  onSessionExpired,
}: {
  tenantSlug: string;
  onSessionExpired: () => void;
}) {
  const [flags, setFlags] = useState<AdminFeatureFlags | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<AdminFeatureFlag | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onSessionExpired is stable; effect closes over tenantSlug
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminFeatureFlagsGet(tenantSlug)
      .then((f) => {
        if (!cancelled) setFlags(f);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        setError(
          err instanceof ApiNetworkError
            ? "Couldn't reach the server."
            : err instanceof ApiError && err.status === 502
              ? "Couldn't reach the tenant container."
              : "Couldn't load feature flags.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  async function toggle(flag: AdminFeatureFlag, enabled: boolean) {
    setSaving(flag);
    setError(null);
    try {
      const updated = await adminFeatureFlagsSet(tenantSlug, { flag, enabled });
      setFlags(updated);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError(featureFlagErrorMessage(err));
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-2)' }}>
        feature flags
      </h2>
      <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
        Per-tenant switches. Agent behavior flags take effect on the next agent turn; client
        dashboard lane visibility updates on the next page load — no restart needed.
      </p>
      {loading && flags === null && <p className="field-help">loading…</p>}
      {!loading && error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {flags && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {FEATURE_FLAG_META.map((m) => (
            <label
              key={m.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={flags[m.key]}
                disabled={
                  saving !== null ||
                  (m.key === 'technicianSheetStatusWritebackEnabled' && !flags[m.key]) ||
                  m.key === 'technicianSheetRowProjectionEnabled'
                }
                onChange={(e) => toggle(m.key, e.currentTarget.checked)}
                aria-label={m.label}
                style={{ width: 18, height: 18, marginTop: 2 }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ color: 'var(--ink)', fontWeight: 500 }}>
                  {m.label}{' '}
                  <span className="mono" style={{ color: 'var(--muted)' }}>
                    {flags[m.key] ? 'on' : 'off'}
                  </span>
                </span>
                <span className="field-help">{m.help}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function featureFlagErrorMessage(err: unknown): string {
  if (err instanceof ApiNetworkError) return "Couldn't reach the server.";
  if (err instanceof ApiError) {
    const body = parseErrorBody(err.bodyText);
    const ownerContributionMessage = ownerContributionSetupIncompleteMessage(body);
    if (ownerContributionMessage) return ownerContributionMessage;
    if (err.status === 502) return "Couldn't reach the tenant container.";
  }
  return "Couldn't update the feature flag.";
}

function ownerContributionSetupIncompleteMessage(
  body: Record<string, unknown> | null,
): string | null {
  if (body?.error === 'etransfer_owner_contribution_setup_incomplete') {
    const fields = [...readStringArray(body.missingFields), ...readStringArray(body.blockedFields)];
    const suffix = fields.length > 0 ? ` Check: ${fields.join(', ')}.` : '';
    return `Can't enable e-transfer owner contributions until its safety setup is complete.${suffix}`;
  }
  if (typeof body?.body === 'string') {
    return ownerContributionSetupIncompleteMessage(parseErrorBody(body.body));
  }
  return null;
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((field): field is string => typeof field === 'string')
    : [];
}

function JurisdictionPanel({
  tenantSlug,
  onSessionExpired,
}: {
  tenantSlug: string;
  onSessionExpired: () => void;
}) {
  const [settings, setSettings] = useState<AdminJurisdictionSettings | null>(null);
  const [jurisdiction, setJurisdiction] = useState<AdminJurisdiction>({
    country: null,
    region: null,
    county: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: effect reloads when tenant changes; session handler is stable
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotice(null);
    adminJurisdictionGet(tenantSlug)
      .then((next) => {
        if (cancelled) return;
        setSettings(next);
        setJurisdiction({
          country: next.jurisdiction.country ?? null,
          region: next.jurisdiction.region ?? null,
          county: next.jurisdiction.county ?? null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        setError(adminForwardError(err, "Couldn't load notice jurisdiction."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const next = await adminJurisdictionSet(tenantSlug, {
        country: normalizeOptional(jurisdiction.country)?.toUpperCase() ?? null,
        region: normalizeOptional(jurisdiction.region)?.toUpperCase() ?? null,
        county: null,
      });
      setSettings(next);
      setJurisdiction({
        country: next.jurisdiction.country ?? null,
        region: next.jurisdiction.region ?? null,
        county: next.jurisdiction.county ?? null,
      });
      setNotice('Jurisdiction saved. Tenant dashboard Notices updates on the next reload.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError(adminForwardError(err, "Couldn't save notice jurisdiction."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-2)' }}>
        owner dashboard jurisdiction
      </h2>
      <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
        Controls the tenant Notices dropdown and the view-only Configuration jurisdiction card.
        Leave country empty to hide Notices for this tenant.
      </p>
      {loading && settings === null && <p className="field-help">loading…</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <output className="field-help" style={{ color: 'var(--green)' }}>
          {notice}
        </output>
      )}
      <div style={{ display: 'grid', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        <label className="field-label">
          Country
          <select
            value={jurisdiction.country ?? ''}
            onChange={(e) =>
              setJurisdiction((j) => ({ ...j, country: e.currentTarget.value || null }))
            }
            disabled={saving}
          >
            <option value="">Not configured</option>
            <option value="CA">Canada</option>
            <option value="US">United States</option>
          </select>
        </label>
        <label className="field-label">
          Region / province
          <input
            value={jurisdiction.region ?? ''}
            onChange={(e) => setJurisdiction((j) => ({ ...j, region: e.currentTarget.value }))}
            disabled={saving || !jurisdiction.country}
            placeholder="ON"
          />
        </label>
        <label className="field-label">
          County
          <input value={jurisdiction.county ?? ''} disabled placeholder="Not used yet" />
        </label>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn-primary"
            onClick={save}
            disabled={saving || loading}
            style={{ padding: '8px 14px', fontSize: 'var(--fs-small)' }}
          >
            {saving ? 'saving…' : 'save jurisdiction'}
          </button>
        </div>
      </div>
      {settings && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <p className="field-help" style={{ marginBottom: 'var(--space-2)' }}>
            Resolved notice forms visible to this tenant:
          </p>
          {settings.forms.length ? (
            <ul className="field-help">
              {settings.forms.map((form) => (
                <li key={`${form.noticeType}:${form.formVersion}`}>
                  <strong>{form.noticeType}</strong> — {form.name} ({form.formVersion})
                </li>
              ))}
            </ul>
          ) : (
            <p className="field-help">No forms resolved. Notices will be hidden.</p>
          )}
        </div>
      )}
    </div>
  );
}

function OperationalConfigPanel({
  tenantSlug,
  onSessionExpired,
}: {
  tenantSlug: string;
  onSessionExpired: () => void;
}) {
  const [config, setConfig] = useState<AdminOperationalConfigView | null>(null);
  const [vendorSmsFromNumber, setVendorSmsFromNumber] = useState('');
  const [matrixJson, setMatrixJson] = useState('{}');
  const [accountingMode, setAccountingMode] = useState<'as_received' | 'daily_digest'>(
    'as_received',
  );
  const [accountingDays, setAccountingDays] = useState('');
  const [accountingTime, setAccountingTime] = useState('12:00');
  const [utilitySplitEnabled, setUtilitySplitEnabled] = useState(false);
  const [utilityGlAccountId, setUtilityGlAccountId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function hydrate(next: AdminOperationalConfigView) {
    setConfig(next);
    setVendorSmsFromNumber(
      next.textNumbers.vendors === 'Not configured' ? '' : next.textNumbers.vendors,
    );
    setMatrixJson(JSON.stringify(matrixFromView(next), null, 2));
    setAccountingMode(next.accounting.mode);
    setAccountingDays(next.accounting.days.join(', '));
    setAccountingTime(next.accounting.localTime || '12:00');
    setUtilitySplitEnabled(Boolean(next.features.utilitySplit));
    setUtilityGlAccountId(
      typeof next.features.utilityGlAccountId === 'number'
        ? String(next.features.utilityGlAccountId)
        : '',
    );
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: effect reloads when tenant changes; session handler is stable
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotice(null);
    adminOperationalConfigGet(tenantSlug)
      .then((next) => {
        if (!cancelled) hydrate(next);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        setError(adminForwardError(err, "Couldn't load operational configuration."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  async function save() {
    let parsedMatrix: Record<string, unknown> | null = null;
    try {
      parsedMatrix = matrixJson.trim() ? (JSON.parse(matrixJson) as Record<string, unknown>) : null;
    } catch {
      setError('Workflow matrix must be valid JSON.');
      return;
    }
    const parsedUtilityGlAccountId = parseOptionalPositiveInteger(utilityGlAccountId);
    if (parsedUtilityGlAccountId === undefined) {
      setError('Utility GL account id must be a positive integer or blank.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const next = await adminOperationalConfigSet(tenantSlug, {
        vendorSmsFromNumber: normalizeOptional(vendorSmsFromNumber),
        maintenanceWorkflowMatrix: parsedMatrix,
        accountingNotificationMode: accountingMode,
        accountingNotificationDays: accountingDays
          .split(',')
          .map((day) => day.trim())
          .filter(Boolean),
        accountingNotificationLocalTime: accountingTime || '12:00',
        utilitySplitEnabled,
        utilityGlAccountId: parsedUtilityGlAccountId,
      });
      hydrate(next);
      setNotice('Operational configuration saved.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError(adminForwardError(err, "Couldn't save operational configuration."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-2)' }}>
        owner dashboard configuration
      </h2>
      <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
        Support-authored view model for the read-only Configuration page. This does not start new
        schedulers; it documents how the tenant is configured today.
      </p>
      {loading && config === null && <p className="field-help">loading…</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <output className="field-help" style={{ color: 'var(--green)' }}>
          {notice}
        </output>
      )}
      {config && (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <div className="field-help">
            PMS {config.status.pms} · email {config.status.email} · calendar{' '}
            {config.status.calendar} · current unit count {config.noticeForms.length} notice forms
          </div>
          <label className="field-label">
            Vendor SMS number
            <input
              value={vendorSmsFromNumber}
              onChange={(e) => setVendorSmsFromNumber(e.currentTarget.value)}
              disabled={saving}
              placeholder="+14165550123"
            />
          </label>
          <label className="field-label">
            Maintenance workflow matrix JSON
            <textarea
              value={matrixJson}
              onChange={(e) => setMatrixJson(e.currentTarget.value)}
              disabled={saving}
              rows={12}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </label>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}
          >
            <label className="field-label">
              Accounting notifications
              <select
                value={accountingMode}
                onChange={(e) =>
                  setAccountingMode(e.currentTarget.value as 'as_received' | 'daily_digest')
                }
                disabled={saving}
              >
                <option value="as_received">As received</option>
                <option value="daily_digest">Daily digest</option>
              </select>
            </label>
            <label className="field-label">
              Digest days
              <input
                value={accountingDays}
                onChange={(e) => setAccountingDays(e.currentTarget.value)}
                disabled={saving}
                placeholder="monday, wednesday, friday"
              />
            </label>
            <label className="field-label">
              Local send time
              <input
                value={accountingTime}
                onChange={(e) => setAccountingTime(e.currentTarget.value)}
                disabled={saving}
                placeholder="12:00"
              />
            </label>
          </div>
          <label
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'center',
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={utilitySplitEnabled}
              onChange={(e) => setUtilitySplitEnabled(e.currentTarget.checked)}
              disabled={saving}
              style={{ width: 18, height: 18 }}
            />
            <span>Show utility split connection as enabled</span>
          </label>
          <label className="field-label">
            Utility charge GL account id
            <input
              value={utilityGlAccountId}
              onChange={(e) => setUtilityGlAccountId(e.currentTarget.value)}
              disabled={saving}
              placeholder="Buildium GL account id"
            />
          </label>
          <button
            type="button"
            className="btn-primary"
            onClick={save}
            disabled={saving || loading}
            style={{ padding: '8px 14px', fontSize: 'var(--fs-small)', justifySelf: 'start' }}
          >
            {saving ? 'saving…' : 'save configuration'}
          </button>
        </div>
      )}
    </div>
  );
}

function BillingAdminPanel({
  tenantSlug,
  onSessionExpired,
}: {
  tenantSlug: string;
  onSessionExpired: () => void;
}) {
  const [billing, setBilling] = useState<AdminBillingView | null>(null);
  const [planType, setPlanType] = useState<'per_unit' | 'fixed_monthly' | 'custom'>('per_unit');
  const [perUnitCents, setPerUnitCents] = useState('');
  const [fixedMonthlyCents, setFixedMonthlyCents] = useState('');
  const [billedUnits, setBilledUnits] = useState('0');
  const [nextBillingDate, setNextBillingDate] = useState('');
  const [billingContactEmail, setBillingContactEmail] = useState('');
  const [billingTelegramChatId, setBillingTelegramChatId] = useState('');
  const [stripeBillingEnabled, setStripeBillingEnabled] = useState(false);
  const [paymentPaidAt, setPaymentPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentUnits, setPaymentUnits] = useState('0');
  const [paymentNote, setPaymentNote] = useState('');
  const agreementInputRef = useRef<HTMLInputElement | null>(null);
  const receiptInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function hydrate(next: AdminBillingView, initializePayment = false) {
    setBilling(next);
    setPlanType(next.planType);
    setPerUnitCents(next.perUnitCents == null ? '' : String(next.perUnitCents));
    setFixedMonthlyCents(String(next.fixedMonthlyCents ?? ''));
    setBilledUnits(String(next.billedUnits));
    setNextBillingDate(next.nextBillingDate ?? '');
    if (initializePayment) {
      setPaymentUnits(String(next.billedUnits || next.currentPmsUnits || 0));
      setPaymentAmount(centsToDollars(next.nextPaymentAmountCents ?? 0));
    }
    setBillingContactEmail(next.billingContactEmail ?? '');
    setBillingTelegramChatId(String(next.billingContactTelegramChatId ?? ''));
    setStripeBillingEnabled(next.stripeBillingEnabled);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: effect reloads when tenant changes; session handler is stable
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotice(null);
    adminBillingGet(tenantSlug)
      .then((next) => {
        if (!cancelled) hydrate(next, true);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        setError(adminForwardError(err, "Couldn't load billing settings."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  async function persistBillingSettings() {
    return adminBillingSet(tenantSlug, {
      planType,
      perUnitCents: planType === 'per_unit' ? Number(perUnitCents) : null,
      fixedMonthlyCents: planType === 'fixed_monthly' ? Number(fixedMonthlyCents) : null,
      billedUnits: Number(billedUnits || 0),
      nextBillingDate: normalizeOptional(nextBillingDate),
      billingContactEmail: normalizeOptional(billingContactEmail),
      billingContactTelegramChatId: billingTelegramChatId ? Number(billingTelegramChatId) : null,
      stripeBillingEnabled,
    });
  }

  async function saveConfig() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const next = await persistBillingSettings();
      hydrate(next);
      setNotice('Billing settings saved.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError(adminForwardError(err, "Couldn't save billing settings."));
    } finally {
      setSaving(false);
    }
  }

  const pricingIsValid =
    planType === 'custom' ||
    (planType === 'per_unit'
      ? /^\d+$/.test(perUnitCents) && Number(perUnitCents) > 0
      : /^\d+$/.test(fixedMonthlyCents) && Number(fixedMonthlyCents) > 0);

  async function uploadAgreement() {
    const file = agreementInputRef.current?.files?.[0];
    if (!file) {
      setError('Choose an agreement PDF first.');
      return;
    }
    let settingsSaved = false;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (!pricingIsValid)
        throw new Error('Correct billing pricing before uploading an agreement.');
      await persistBillingSettings();
      settingsSaved = true;
      const next = await adminBillingAgreementUpload(tenantSlug, file);
      hydrate(next);
      if (agreementInputRef.current) agreementInputRef.current.value = '';
      setNotice('Agreement uploaded.');
    } catch (err) {
      if (settingsSaved)
        setNotice(
          'Billing settings saved; agreement upload failed. Your other entries are retained.',
        );
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError(adminForwardError(err, "Couldn't upload agreement PDF."));
    } finally {
      setSaving(false);
    }
  }

  async function addPayment() {
    const receipt = receiptInputRef.current?.files?.[0];
    if (!receipt) {
      setError('Choose a receipt PDF first.');
      return;
    }
    let settingsSaved = false;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (!pricingIsValid) throw new Error('Correct billing pricing before uploading a receipt.');
      await persistBillingSettings();
      settingsSaved = true;
      const next = await adminBillingPaymentCreate(tenantSlug, {
        paidAt: paymentPaidAt,
        amountCents: dollarsToCents(paymentAmount),
        billedUnits: Number(paymentUnits || 0),
        perUnitCents: Number(perUnitCents),
        receipt,
        note: paymentNote,
      });
      hydrate(next);
      if (receiptInputRef.current) receiptInputRef.current.value = '';
      setPaymentNote('');
      setNotice('Payment receipt uploaded.');
    } catch (err) {
      if (settingsSaved)
        setNotice(
          'Billing settings saved; receipt upload failed. Your other entries are retained.',
        );
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError(adminForwardError(err, "Couldn't upload payment receipt."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-2)' }}>
        owner dashboard billing
      </h2>
      <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
        Controls the view-only Billing page: cycle unit snapshot, rate, agreement PDF, and receipt
        history. Signed customer links are generated by the tenant API.
      </p>
      {loading && billing === null && <p className="field-help">loading…</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <output className="field-help" style={{ color: 'var(--green)' }}>
          {notice}
        </output>
      )}
      {billing && (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {billing.tenantProjectionUnavailable && (
            <p className="field-error" role="alert">
              Tenant billing projection is unreachable. Canonical control-plane values are shown;
              the reconciler will retry replication.
            </p>
          )}
          <div className="field-help">
            Current PMS units: {billing.currentPmsUnits} · next payment{' '}
            {billing.nextPaymentAmountCents == null
              ? 'custom'
              : formatUsd(billing.nextPaymentAmountCents)}
          </div>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}
          >
            <label className="field-label">
              Plan
              <select
                value={planType}
                onChange={(e) =>
                  setPlanType(e.currentTarget.value as 'per_unit' | 'fixed_monthly' | 'custom')
                }
                disabled={saving || stripeBillingEnabled}
              >
                <option value="per_unit">Per unit</option>
                <option value="fixed_monthly">Fixed monthly</option>
                <option value="custom">Custom / support invoiced</option>
              </select>
            </label>
            {planType === 'per_unit' && (
              <label className="field-label">
                Per-unit rate (cents USD)
                <input
                  value={perUnitCents}
                  onChange={(e) => setPerUnitCents(e.currentTarget.value)}
                  disabled={saving}
                  inputMode="numeric"
                />
              </label>
            )}
            {planType === 'fixed_monthly' && (
              <label className="field-label">
                Fixed monthly (cents USD)
                <input
                  value={fixedMonthlyCents}
                  onChange={(e) => setFixedMonthlyCents(e.currentTarget.value)}
                  disabled={saving}
                  inputMode="numeric"
                />
              </label>
            )}
            <label className="field-label">
              Billed units snapshot
              <input
                value={billedUnits}
                onChange={(e) => setBilledUnits(e.currentTarget.value)}
                disabled={saving}
                inputMode="numeric"
              />
            </label>
            <label className="field-label">
              Next billing date
              <input
                type="date"
                value={nextBillingDate}
                onChange={(e) => setNextBillingDate(e.currentTarget.value)}
                disabled={saving}
              />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <label className="field-label">
              Billing email
              <input
                type="email"
                value={billingContactEmail}
                onChange={(e) => setBillingContactEmail(e.currentTarget.value)}
                disabled={saving}
              />
            </label>
            <label className="field-label">
              Billing Telegram chat ID
              <input
                value={billingTelegramChatId}
                onChange={(e) => setBillingTelegramChatId(e.currentTarget.value)}
                disabled={saving}
                inputMode="numeric"
              />
            </label>
          </div>
          <label className="field-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={stripeBillingEnabled}
              onChange={(e) => setStripeBillingEnabled(e.currentTarget.checked)}
              disabled={saving || !billing.stripeBillingRolloutAllowed}
            />
            Enable Stripe billing (sandbox rollout only)
          </label>
          <button
            type="button"
            className="btn-primary"
            onClick={saveConfig}
            disabled={saving || !pricingIsValid}
            style={{ padding: '8px 14px', fontSize: 'var(--fs-small)', justifySelf: 'start' }}
          >
            {saving ? 'saving…' : 'save billing config'}
          </button>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--space-3)' }}>
            <label className="field-label">
              Agreement PDF
              <input
                ref={agreementInputRef}
                type="file"
                accept="application/pdf"
                disabled={saving}
              />
            </label>
            <button
              type="button"
              className="btn-secondary"
              onClick={uploadAgreement}
              disabled={saving}
              style={{ alignSelf: 'end', padding: '8px 14px', fontSize: 'var(--fs-small)' }}
            >
              upload agreement
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 'var(--space-3)',
              alignItems: 'end',
            }}
          >
            <label className="field-label">
              Paid at
              <input
                type="date"
                value={paymentPaidAt}
                onChange={(e) => setPaymentPaidAt(e.currentTarget.value)}
                disabled={saving}
              />
            </label>
            <label className="field-label">
              Amount
              <input
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.currentTarget.value)}
                disabled={saving}
                placeholder="300.00"
              />
            </label>
            <label className="field-label">
              Units
              <input
                value={paymentUnits}
                onChange={(e) => setPaymentUnits(e.currentTarget.value)}
                disabled={saving}
                inputMode="numeric"
              />
            </label>
            <label className="field-label">
              Receipt PDF
              <input ref={receiptInputRef} type="file" accept="application/pdf" disabled={saving} />
            </label>
          </div>
          <label className="field-label">
            Adjustment note
            <input
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.currentTarget.value)}
              disabled={saving}
              placeholder="Optional"
            />
          </label>
          <button
            type="button"
            className="btn-secondary"
            onClick={addPayment}
            disabled={saving}
            style={{ padding: '8px 14px', fontSize: 'var(--fs-small)', justifySelf: 'start' }}
          >
            add payment receipt
          </button>
          <div>
            <p className="field-help" style={{ marginBottom: 'var(--space-2)' }}>
              Recent payments
            </p>
            {billing.payments.length ? (
              <ul className="field-help">
                {billing.payments.slice(0, 5).map((payment) => (
                  <li key={payment.id}>
                    {payment.paidAt} · {formatUsd(payment.amountCents)} ·{' '}
                    {payment.source === 'stripe' ? 'Stripe' : 'manual'}{' '}
                    {payment.entryType === 'refund' ? 'refund' : 'payment'}
                    {payment.billedUnits == null ? '' : ` · ${payment.billedUnits} units`}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="field-help">No payment receipts uploaded yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function normalizeOptional(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed ? trimmed : null;
}

function parseOptionalPositiveInteger(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function matrixFromView(view: AdminOperationalConfigView): Record<string, unknown> {
  return Object.fromEntries(
    view.maintenanceWorkflow.map((step) => [
      step.key,
      {
        channels: step.channels,
        sourceOfTruth: step.sourceOfTruth,
        sheetLink: step.sheetLink,
        sharedEmail: step.sharedEmail,
      },
    ]),
  );
}

function dollarsToCents(value: string): number {
  const normalized = value.replace(/[$,\s]/g, '');
  const dollars = Number(normalized || 0);
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;
}

function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);
}

function formatUsd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function adminForwardError(err: unknown, fallback: string): string {
  if (err instanceof ApiNetworkError) return "Couldn't reach the server.";
  if (err instanceof ApiError && err.status === 502) return "Couldn't reach the tenant container.";
  if (err instanceof ApiError && err.status === 400)
    return 'The tenant rejected the submitted values.';
  return fallback;
}

function AgentModelPanel({
  tenantSlug,
  onSessionExpired,
}: {
  tenantSlug: string;
  onSessionExpired: () => void;
}) {
  const [settings, setSettings] = useState<AdminAgentModelSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<AdminAgentModelProfileId | null>(null);
  const [testing, setTesting] = useState<AdminAgentModelProfileId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [kimiApiKey, setKimiApiKey] = useState('');
  const [openRouterApiKey, setOpenRouterApiKey] = useState('');
  const [openRouterModel, setOpenRouterModel] = useState('google/gemini-3.8-flash');

  // biome-ignore lint/correctness/useExhaustiveDependencies: onSessionExpired is stable; effect closes over tenantSlug
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminAgentModelGet(tenantSlug)
      .then((next) => {
        if (!cancelled) {
          setSettings(next);
          const openRouter = next.profiles.find(
            (profile) => profile.id === 'openrouter-minimax-m3',
          );
          if (openRouter?.model) setOpenRouterModel(openRouter.model);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        setError(
          err instanceof ApiNetworkError
            ? "Couldn't reach the server."
            : err instanceof ApiError && err.status === 502
              ? "Couldn't reach the tenant container."
              : "Couldn't load agent model settings.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  async function activate(profile: AdminAgentModelProfileId) {
    setSaving(profile);
    setError(null);
    setNotice(null);
    try {
      const next = await adminAgentModelSet(tenantSlug, { activeProfile: profile });
      setSettings(next);
      setNotice('Agent model updated. The next agent turn will use the active profile.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else if (err instanceof ApiError && err.status === 400) {
        setError(
          profile === 'kimi-k2.7-code'
            ? 'Kimi could not be activated. Add or test a valid Kimi API key first.'
            : profile === 'openrouter-minimax-m3'
              ? 'OpenRouter Gemini 3.8 Flash could not be activated. Add or test a valid OpenRouter API key first.'
              : "Couldn't update the active model.",
        );
      } else setError("Couldn't update the active model.");
    } finally {
      setSaving(null);
    }
  }

  async function saveAnthropicKey() {
    const submitted = anthropicApiKey;
    const key = submitted.trim();
    if (!key || saving !== null) return;
    setSaving('anthropic-haiku-4-5');
    setError(null);
    setNotice(null);
    try {
      setSettings(await adminAgentModelSet(tenantSlug, { anthropicApiKey: key }));
      setAnthropicApiKey((current) => (current === submitted ? '' : current));
      setNotice('Anthropic key saved securely. New Anthropic requests use the updated key.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError("Couldn't save the Anthropic key.");
    } finally {
      setSaving(null);
    }
  }

  async function saveKimiKey() {
    const key = kimiApiKey.trim();
    if (!key) return;
    setSaving('kimi-k2.7-code');
    setError(null);
    setNotice(null);
    try {
      const next = await adminAgentModelSet(tenantSlug, { kimiApiKey: key });
      setSettings(next);
      setKimiApiKey((current) => (current === kimiApiKey ? '' : current));
      setNotice('Kimi key saved. It is write-only and is not returned by the API.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError("Couldn't save the Kimi key.");
    } finally {
      setSaving(null);
    }
  }

  async function saveOpenRouterKey() {
    const key = openRouterApiKey.trim();
    const model = openRouterModel.trim();
    if (!key && !model) return;
    setSaving('openrouter-minimax-m3');
    setError(null);
    setNotice(null);
    try {
      const next = await adminAgentModelSet(tenantSlug, {
        ...(key ? { openRouterApiKey: key } : {}),
        ...(model ? { openRouterModel: model } : {}),
      });
      setSettings(next);
      setOpenRouterApiKey((current) => (current === openRouterApiKey ? '' : current));
      setNotice('OpenRouter settings saved. The API key is write-only and is not returned.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError("Couldn't save the OpenRouter settings.");
    } finally {
      setSaving(null);
    }
  }

  async function testProfile(profile: AdminAgentModelProfileId) {
    setTesting(profile);
    setError(null);
    setNotice(null);
    try {
      await adminAgentModelTest(tenantSlug, {
        profile,
        ...(profile === 'kimi-k2.7-code' && kimiApiKey.trim()
          ? { kimiApiKey: kimiApiKey.trim() }
          : {}),
        ...(profile === 'openrouter-minimax-m3' && openRouterApiKey.trim()
          ? { openRouterApiKey: openRouterApiKey.trim() }
          : {}),
        ...(profile === 'openrouter-minimax-m3' && openRouterModel.trim()
          ? { openRouterModel: openRouterModel.trim() }
          : {}),
      });
      setNotice(
        profile === 'kimi-k2.7-code'
          ? 'Kimi key test passed.'
          : profile === 'openrouter-minimax-m3'
            ? 'OpenRouter Gemini 3.8 Flash key test passed.'
            : 'Haiku profile is configured.',
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else
        setError(
          profile === 'kimi-k2.7-code'
            ? 'Kimi key test failed.'
            : profile === 'openrouter-minimax-m3'
              ? 'OpenRouter Gemini 3.8 Flash key test failed.'
              : 'Haiku profile is not configured.',
        );
    } finally {
      setTesting(null);
    }
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-2)' }}>
        agent model
      </h2>
      <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
        Per-tenant model switcher. Changes apply on the next agent turn; API keys are write-only.
      </p>
      {loading && settings === null && <p className="field-help">loading…</p>}
      {!loading && error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <output className="field-help" style={{ color: 'var(--green)' }}>
          {notice}
        </output>
      )}
      {settings && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {settings.profiles.map((profile) => (
            <div
              key={profile.id}
              style={{
                display: 'grid',
                gap: 'var(--space-2)',
                padding: 'var(--space-4)',
                border: `1px solid ${profile.active ? 'var(--green)' : 'var(--rule)'}`,
                borderRadius: 'var(--radius-lg, 12px)',
                background: profile.active ? 'rgba(16,132,108,.06)' : 'transparent',
              }}
            >
              <label style={{ display: 'flex', gap: 'var(--space-3)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={`agent-model-${tenantSlug}`}
                  checked={profile.active}
                  disabled={saving !== null}
                  onChange={() => activate(profile.id)}
                  aria-label={`Use ${profile.label}`}
                  style={{ width: 18, height: 18, marginTop: 2 }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ color: 'var(--ink)', fontWeight: 600 }}>
                    {profile.label}{' '}
                    <span className="mono" style={{ color: 'var(--muted)' }}>
                      {profile.active ? 'active' : 'inactive'}
                    </span>
                  </span>
                  <span className="field-help">
                    {profile.provider} · {profile.model}
                    {profile.baseUrl ? ` · ${profile.baseUrl}` : ''}
                  </span>
                  <span className="field-help">
                    key {profile.keyConfigured ? 'configured' : 'missing'} · active next turn
                  </span>
                </span>
              </label>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={testing !== null}
                  onClick={() => testProfile(profile.id)}
                  style={{ padding: '7px 12px', fontSize: 'var(--fs-small)' }}
                >
                  {testing === profile.id ? 'testing…' : 'test profile'}
                </button>
              </div>
            </div>
          ))}
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <label className="field-label" htmlFor={`anthropic-key-${tenantSlug}`}>
              Anthropic API key
            </label>
            <input
              id={`anthropic-key-${tenantSlug}`}
              type="password"
              value={anthropicApiKey}
              onChange={(e) => setAnthropicApiKey(e.currentTarget.value)}
              autoComplete="off"
              placeholder="paste key to save"
            />
            <button
              type="button"
              className="btn-secondary"
              disabled={!anthropicApiKey.trim() || saving !== null}
              onClick={saveAnthropicKey}
            >
              save Anthropic key
            </button>
            <label className="field-label" htmlFor={`kimi-key-${tenantSlug}`}>
              Kimi API key
            </label>
            <input
              id={`kimi-key-${tenantSlug}`}
              type="password"
              value={kimiApiKey}
              onChange={(e) => setKimiApiKey(e.currentTarget.value)}
              placeholder="paste key to save or test"
              autoComplete="off"
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-secondary"
                disabled={!kimiApiKey.trim() || saving !== null}
                onClick={saveKimiKey}
                style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
              >
                {saving === 'kimi-k2.7-code' ? 'saving…' : 'save Kimi key'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={!kimiApiKey.trim() || testing !== null}
                onClick={() => testProfile('kimi-k2.7-code')}
                style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
              >
                test pasted key
              </button>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <label className="field-label" htmlFor={`openrouter-model-${tenantSlug}`}>
              OpenRouter model
            </label>
            <input
              id={`openrouter-model-${tenantSlug}`}
              type="text"
              value={openRouterModel}
              onChange={(e) => setOpenRouterModel(e.currentTarget.value)}
              placeholder="google/gemini-3.8-flash"
              autoComplete="off"
            />
            <label className="field-label" htmlFor={`openrouter-key-${tenantSlug}`}>
              OpenRouter API key
            </label>
            <input
              id={`openrouter-key-${tenantSlug}`}
              type="password"
              value={openRouterApiKey}
              onChange={(e) => setOpenRouterApiKey(e.currentTarget.value)}
              placeholder="paste key to save or test"
              autoComplete="off"
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-secondary"
                disabled={(!openRouterApiKey.trim() && !openRouterModel.trim()) || saving !== null}
                onClick={saveOpenRouterKey}
                style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
              >
                {saving === 'openrouter-minimax-m3' ? 'saving…' : 'save OpenRouter settings'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={!openRouterApiKey.trim() || testing !== null}
                onClick={() => testProfile('openrouter-minimax-m3')}
                style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
              >
                test pasted key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SetupRow({ label, status }: { label: string; status: 'done' | 'awaiting' }) {
  const isDone = status === 'done';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-2) 0',
      }}
    >
      <span
        aria-label={status}
        style={{
          width: 16,
          textAlign: 'center',
          color: isDone ? 'var(--green)' : 'var(--muted)',
        }}
      >
        {isDone ? '✓' : '—'}
      </span>
      <span style={{ color: 'var(--ink)' }}>{label}</span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          marginLeft: 'auto',
        }}
      >
        {isDone ? 'connected' : 'awaiting PM'}
      </span>
    </div>
  );
}

function apiErrorStatus(err: unknown): number | null {
  if (err instanceof ApiError) return err.status;
  if (typeof err === 'object' && err !== null && 'status' in err) {
    const status = (err as { status?: unknown }).status;
    return typeof status === 'number' ? status : null;
  }
  return null;
}

function emailRoutingErrorMessage(err: unknown): string {
  if (err instanceof ApiNetworkError) return "Couldn't reach the server.";
  if (err instanceof ApiError) {
    const body = parseErrorBody(err.bodyText);
    if (err.status === 422 && body?.status === 'cloudflare_failed') {
      if (body.errorClass === 'destination_not_verified') {
        return 'Cloudflare rejected that inbox because it is not verified yet. Verify the destination in Cloudflare Email Routing, then save again.';
      }
      if (body.errorClass === 'invalid_destination') {
        return 'Cloudflare rejected that forwarding inbox. Check the email address and try again.';
      }
      return "Cloudflare couldn't update the route. Try again after checking Email Routing.";
    }
    if (err.status === 502) return "Couldn't reach the tenant container.";
    if (err.status === 400) return 'Enter a valid forwarding email address.';
  }
  return "Couldn't update email routing.";
}

function parseErrorBody(bodyText: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(bodyText) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function PmsSection({
  tenantSlug,
  tenantStatus,
  onSessionExpired,
  onViewPmConversation,
}: {
  tenantSlug: string;
  tenantStatus: ClientStatus;
  onSessionExpired: () => void;
  onViewPmConversation: (chatId: string, groupKind?: PmGroupKind | null) => void;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [inviteRole, setInviteRole] = useState<UserRole>('property_manager');
  const [pmDisplayName, setPmDisplayName] = useState('');
  const [pmPhone, setPmPhone] = useState('');
  const [pmTelegramUsername, setPmTelegramUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminPmInviteCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [bindings, setBindings] = useState<AdminPmBindingPublic[] | null>(null);
  const [invoiceRecipients, setInvoiceRecipients] = useState<
    AdminInvoiceCardRecipientPublic[] | null
  >(null);
  const [bindingsError, setBindingsError] = useState<string | null>(null);
  const [invoiceRecipientsError, setInvoiceRecipientsError] = useState<string | null>(null);
  const [bindingsLoading, setBindingsLoading] = useState(false);
  const [recipientSaving, setRecipientSaving] = useState<string | null>(null);
  const [groups, setGroups] = useState<AdminPmGroupBindingPublic[]>([]);
  const [memberships, setMemberships] = useState<AdminPmGroupMembershipPublic[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [membershipSaving, setMembershipSaving] = useState<string | null>(null);

  // Fetch bindings on mount + after every successful invite create. Bindings
  // only populate when the PM completes /start <token> + taps Confirm, so
  // the count won't change immediately after creating an invite — but we
  // refetch so a recent confirmed PM lands in the list on every form submit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: result.token is the intentional refetch trigger; onSessionExpired is stable
  useEffect(() => {
    let cancelled = false;
    setBindingsLoading(true);
    setGroupsLoading(true);
    setBindingsError(null);
    setInvoiceRecipientsError(null);
    Promise.allSettled([
      adminPmBindingsList(tenantSlug),
      adminInvoiceCardRecipientsList(tenantSlug),
    ])
      .then(([bindingsResult, recipientsResult]) => {
        if (cancelled) return;
        if (bindingsResult.status === 'fulfilled') {
          setBindings(bindingsResult.value);
        } else if (apiErrorStatus(bindingsResult.reason) === 401) {
          onSessionExpired();
          return;
        } else {
          setBindingsError(
            apiErrorStatus(bindingsResult.reason) === 502
              ? "Couldn't reach the tenant team-member bindings."
              : 'Could not load team members.',
          );
        }
        if (recipientsResult.status === 'fulfilled') {
          setInvoiceRecipients(recipientsResult.value);
        } else if (apiErrorStatus(recipientsResult.reason) === 401) {
          onSessionExpired();
          return;
        } else {
          setInvoiceRecipientsError(
            apiErrorStatus(recipientsResult.reason) === 502
              ? "Couldn't reach invoice-card settings for this tenant."
              : 'Could not load invoice-card settings.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setBindingsLoading(false);
      });
    adminPmGroupsGet(tenantSlug)
      .then((state) => {
        if (!cancelled) {
          setGroups(state.groups);
          setMemberships(state.memberships);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) onSessionExpired();
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // result.token rotates after each successful create, so refetching keys
    // off it naturally.
  }, [tenantSlug, onSessionExpired, result?.token]);

  const canSubmit =
    tenantStatus === 'live' &&
    pmDisplayName.trim().length > 0 &&
    /^\+[1-9]\d{1,14}$/.test(pmPhone.trim());

  function reset() {
    setFormOpen(false);
    setInviteRole('property_manager');
    setPmDisplayName('');
    setPmPhone('');
    setPmTelegramUsername('');
    setError(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminPmInviteCreate({
        tenantSlug,
        pmDisplayName: pmDisplayName.trim(),
        pmPhoneE164: pmPhone.trim(),
        pmTelegramUsername: pmTelegramUsername.trim() || undefined,
        userRole: inviteRole,
      });
      setResult(res);
      reset();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      if (err instanceof ApiError && err.status === 502) {
        setError("Couldn't reach the tenant container — provisioning may not be complete yet.");
      } else if (err instanceof ApiError && err.status === 409) {
        setError('Tenant is not live yet — wait for provisioning to finish.');
      } else if (err instanceof ApiNetworkError) {
        setError("Couldn't reach the server.");
      } else {
        setError('Failed to generate invite.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function copyUrl() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (e.g. http context); fall back to manual select.
    }
  }

  async function toggleInvoiceCards(recipient: AdminInvoiceCardRecipientPublic, enabled: boolean) {
    setRecipientSaving(recipient.id);
    try {
      const updated = await adminInvoiceCardRecipientSet(tenantSlug, {
        tenantPmUserId: recipient.id,
        receivesInvoiceCards: enabled,
      });
      setInvoiceRecipients((prev) => prev?.map((r) => (r.id === updated.id ? updated : r)) ?? prev);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError('Could not update invoice-card routing.');
    } finally {
      setRecipientSaving(null);
    }
  }

  async function setMembership(tenantPmUserId: string, groupKind: PmGroupKind, enabled: boolean) {
    const key = `${tenantPmUserId}:${groupKind}`;
    setMembershipSaving(key);
    try {
      await adminPmGroupMembershipSet({ slug: tenantSlug, tenantPmUserId, groupKind, enabled });
      setMemberships((prev) => {
        const rest = prev.filter(
          (m) => !(m.tenantPmUserId === tenantPmUserId && m.groupKind === groupKind),
        );
        return enabled
          ? [
              ...rest,
              {
                tenantPmUserId,
                groupKind,
                addedAt: new Date().toISOString(),
                addedByTenantPmUserId: null,
              },
            ]
          : rest;
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSessionExpired();
      else setError('Failed to update group attribution.');
    } finally {
      setMembershipSaving(null);
    }
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-4)',
        }}
      >
        <h2 className="h3" style={{ color: 'var(--ink)' }}>
          team members
        </h2>
        {!formOpen && !result && (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={() => {
                setInviteRole('property_manager');
                setFormOpen(true);
              }}
              disabled={tenantStatus !== 'live'}
              className="btn-secondary"
              title={tenantStatus !== 'live' ? 'tenant must be live first' : ''}
              style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
            >
              + invite PM
            </button>
            <button
              type="button"
              onClick={() => {
                setInviteRole('leasing_agent');
                setFormOpen(true);
              }}
              disabled={tenantStatus !== 'live'}
              className="btn-secondary"
              title={tenantStatus !== 'live' ? 'tenant must be live first' : ''}
              style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
            >
              + invite leasing agent
            </button>
          </div>
        )}
      </div>

      {result && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            background: 'var(--paper-subtle, #f7f5f2)',
            borderRadius: 'var(--radius-lg, 12px)',
          }}
        >
          <InviteSmsStatus
            smsStatus={result.smsStatus}
            smsError={result.smsError}
            pmDisplayName={result.pmDisplayName}
            pmPhoneE164={result.pmPhoneE164}
            expiresAt={result.expiresAt}
          />
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              alignItems: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              wordBreak: 'break-all',
              padding: 'var(--space-3)',
              background: 'var(--paper)',
              border: '1px solid var(--ink-faint, #e6e1da)',
              borderRadius: 'var(--radius-md, 8px)',
            }}
          >
            <span style={{ flex: 1 }}>{result.url}</span>
            <button
              type="button"
              onClick={copyUrl}
              className="btn-secondary"
              style={{ padding: '6px 12px', fontSize: 'var(--fs-small)' }}
            >
              {copied ? '✓ copied' : 'copy'}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setResult(null)}
            className="btn-secondary"
            style={{ alignSelf: 'flex-start', padding: '6px 12px', fontSize: 'var(--fs-small)' }}
          >
            done
          </button>
        </div>
      )}

      {formOpen && !result && (
        <form
          onSubmit={submit}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
        >
          <p className="field-help" style={{ margin: 0 }}>
            inviting {roleLabel(inviteRole)}
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span className="field-label">display name</span>
            <input
              type="text"
              value={pmDisplayName}
              disabled={submitting}
              onChange={(e) => setPmDisplayName(e.target.value)}
              // biome-ignore lint/a11y/noAutofocus: form is opened by user clicking + invite PM, autofocus expected
              autoFocus
              maxLength={100}
              required
              className="field-input"
              placeholder="Jane Doe"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span className="field-label">phone (E.164)</span>
            <input
              type="tel"
              value={pmPhone}
              disabled={submitting}
              onChange={(e) => setPmPhone(e.target.value)}
              required
              pattern="^\+[1-9][0-9]{1,14}$"
              className="field-input"
              placeholder="+14155551234"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span className="field-label">telegram username (optional)</span>
            <input
              type="text"
              value={pmTelegramUsername}
              disabled={submitting}
              onChange={(e) => setPmTelegramUsername(e.target.value)}
              maxLength={32}
              pattern="^[a-zA-Z0-9_]{5,32}$"
              className="field-input"
              placeholder="janedoe"
            />
          </label>
          {error && <p className="field-error">{error}</p>}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="btn-primary"
              style={{ padding: '8px 16px' }}
            >
              {submitting ? 'generating…' : 'generate invite link'}
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={submitting}
              className="btn-secondary"
              style={{ padding: '8px 16px' }}
            >
              cancel
            </button>
          </div>
        </form>
      )}

      {!formOpen && !result && (
        <PmBindingsList
          loading={bindingsLoading}
          bindings={bindings}
          invoiceRecipients={invoiceRecipients}
          bindingsError={bindingsError}
          invoiceRecipientsError={invoiceRecipientsError}
          recipientSaving={recipientSaving}
          groups={groups}
          memberships={memberships}
          groupsLoading={groupsLoading}
          membershipSaving={membershipSaving}
          onViewPmConversation={onViewPmConversation}
          onToggleInvoiceCards={toggleInvoiceCards}
          onSetMembership={setMembership}
        />
      )}
    </div>
  );
}

function InviteSmsStatus({
  smsStatus,
  smsError,
  pmDisplayName,
  pmPhoneE164,
  expiresAt,
}: {
  smsStatus: AdminPmInviteCreateResponse['smsStatus'];
  smsError?: string;
  pmDisplayName: string;
  pmPhoneE164: string;
  expiresAt: string;
}) {
  const expires = new Date(expiresAt).toLocaleString();
  if (smsStatus === 'sent') {
    return (
      <p style={{ color: 'var(--ink)' }}>
        <span style={{ color: 'var(--green)', marginRight: 4 }}>✓</span>
        Texted the link to <strong>{pmDisplayName}</strong> at {pmPhoneE164}. They can also tap it
        from here. Expires {expires}.
      </p>
    );
  }
  if (smsStatus === 'failed') {
    return (
      <p style={{ color: 'var(--ink)' }}>
        <span style={{ color: '#c84a3b', marginRight: 4 }}>⚠</span>
        SMS to {pmPhoneE164} failed{smsError ? `: ${smsError}` : ''}. Send the link manually using
        copy below. Expires {expires}.
      </p>
    );
  }
  if (smsStatus === 'skipped') {
    return (
      <p style={{ color: 'var(--ink)' }}>
        <span style={{ color: 'var(--muted)', marginRight: 4 }}>—</span>
        SMS not sent (Twilio not configured on this container). Send this link to{' '}
        <strong>{pmDisplayName}</strong> ({pmPhoneE164}) manually. Expires {expires}.
      </p>
    );
  }
  // Exhaustiveness guard — if a new smsStatus is added to the API type and we
  // forget to handle it here, TS will fail the assignment below at typecheck.
  const _exhaustive: never = smsStatus;
  return _exhaustive;
}

function PmBindingsList({
  loading,
  bindings,
  invoiceRecipients,
  bindingsError,
  invoiceRecipientsError,
  recipientSaving,
  groups,
  memberships,
  groupsLoading,
  membershipSaving,
  onViewPmConversation,
  onToggleInvoiceCards,
  onSetMembership,
}: {
  loading: boolean;
  bindings: AdminPmBindingPublic[] | null;
  invoiceRecipients: AdminInvoiceCardRecipientPublic[] | null;
  bindingsError: string | null;
  invoiceRecipientsError: string | null;
  recipientSaving: string | null;
  groups: AdminPmGroupBindingPublic[];
  memberships: AdminPmGroupMembershipPublic[];
  groupsLoading: boolean;
  membershipSaving: string | null;
  onViewPmConversation: (chatId: string, groupKind?: PmGroupKind | null) => void;
  onToggleInvoiceCards: (recipient: AdminInvoiceCardRecipientPublic, enabled: boolean) => void;
  onSetMembership: (
    tenantPmUserId: string,
    groupKind: PmGroupKind,
    enabled: boolean,
  ) => Promise<void>;
}) {
  if (loading && bindings === null && invoiceRecipients === null) {
    return <p className="field-help">loading bindings…</p>;
  }
  if (bindings === null) {
    return (
      <p className="field-error" role="alert">
        {bindingsError ?? 'Could not load team members.'}
      </p>
    );
  }
  if (bindings.length === 0) {
    return (
      <p className="field-help">
        no team members yet. invite a PM or leasing agent to bind their Telegram chat to this
        tenant.
      </p>
    );
  }
  const activeBindingChatIds = new Set(bindings.map((b) => b.chatId));
  const activeInvoiceRecipients =
    invoiceRecipients?.filter((r) => r.chatId !== null && activeBindingChatIds.has(r.chatId)) ??
    null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <p className="field-help" style={{ margin: 0 }}>
        Connected Telegram groups are approved per group: anyone in the connected group can use that
        lane. Per-person toggles below are attribution/role hints only, not approvals.
        {groupsLoading ? ' Loading group lanes…' : ''}
      </p>
      {bindingsError && (
        <p className="field-error" role="alert" style={{ margin: 0 }}>
          {bindingsError}
        </p>
      )}
      {invoiceRecipientsError && (
        <p className="field-help" style={{ margin: 0 }}>
          {invoiceRecipientsError} Team members are still shown; invoice-card toggles are
          temporarily unavailable.
        </p>
      )}
      {groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {groups.map((g) => (
            <div
              key={`${g.groupKind}:${g.chatId}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--hairline)',
                borderRadius: 8,
                background: 'var(--paper-subtle, #f7f5f2)',
              }}
            >
              <span style={{ fontWeight: 500 }}>{groupLabel(g.groupKind)}</span>
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                {g.title ?? 'Telegram group'}
              </span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                …{g.chatId.slice(-6)}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted)' }}>
                approved group · bound{' '}
                {new Date(g.boundAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              <button
                type="button"
                onClick={() => onViewPmConversation(g.chatId, g.groupKind)}
                className="btn-secondary"
                style={{ padding: '4px 10px', fontSize: 12 }}
                title="View this Telegram group thread"
              >
                view group →
              </button>
            </div>
          ))}
        </div>
      )}
      {(bindings ?? []).map((b) => {
        const stateColor =
          b.confirmationState === 'confirmed'
            ? 'var(--green)'
            : b.confirmationState === 'denied'
              ? 'var(--muted)'
              : 'var(--ink)';
        const primaryName =
          b.displayName?.trim() ||
          (b.telegramUsername ? `@${b.telegramUsername}` : `chat ${b.chatId.slice(-6)}`);
        const secondaryName = b.telegramUsername
          ? `@${b.telegramUsername}`
          : `Telegram chat …${b.chatId.slice(-6)}`;
        return (
          <div
            key={b.chatId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-2) var(--space-3)',
              border: '1px solid var(--hairline)',
              borderRadius: 8,
            }}
          >
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 120 }}>
              <span style={{ fontWeight: 600 }}>{primaryName}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>{secondaryName}</span>
            </span>
            <span
              className="mono"
              style={{ fontSize: 12, color: 'var(--muted)' }}
              title={`chat_id ${b.chatId}`}
            >
              …{b.chatId.slice(-6)}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: 'var(--tracking-mono)',
                textTransform: 'uppercase',
                color: b.userRole === 'leasing_agent' ? '#8a5cf6' : 'var(--muted)',
                border: '1px solid var(--hairline)',
                borderRadius: 999,
                padding: '2px 8px',
              }}
            >
              {roleLabel(b.userRole)}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: 'var(--tracking-mono)',
                textTransform: 'uppercase',
                color: stateColor,
                marginLeft: 'auto',
              }}
            >
              {b.confirmationState}
            </span>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>
              {new Date(b.boundAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}
            </span>
            <button
              type="button"
              onClick={() => onViewPmConversation(b.chatId)}
              className="btn-secondary"
              style={{ padding: '4px 10px', fontSize: 12 }}
              title="View this PM's Telegram DM thread"
            >
              view →
            </button>
            <GroupAccessChecks
              binding={b}
              memberships={memberships}
              savingKey={membershipSaving}
              onSetMembership={onSetMembership}
            />
          </div>
        );
      })}
      {activeInvoiceRecipients && activeInvoiceRecipients.length > 0 && (
        <div
          style={{
            marginTop: 'var(--space-4)',
            paddingTop: 'var(--space-4)',
            borderTop: '1px solid var(--rule)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          <p className="field-help" style={{ margin: 0 }}>
            Invoice cards are opt-in per property manager. New PMs default off.
          </p>
          {activeInvoiceRecipients.map((r) => {
            const disabled =
              r.userRole !== 'property_manager' ||
              r.confirmationState !== 'confirmed' ||
              r.chatId === null ||
              recipientSaving === r.id;
            return (
              <label
                key={r.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-2) var(--space-3)',
                  border: '1px solid var(--rule)',
                  borderRadius: 8,
                }}
              >
                <input
                  type="checkbox"
                  checked={r.receivesInvoiceCards}
                  disabled={disabled}
                  onChange={(e) => onToggleInvoiceCards(r, e.currentTarget.checked)}
                  aria-label={`invoice cards for ${r.displayName ?? r.phoneE164 ?? r.id}`}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ color: 'var(--ink)', fontWeight: 500 }}>
                  {r.displayName ?? r.telegramUsername ?? r.phoneE164 ?? r.id.slice(0, 8)}
                </span>
                <span className="mono" style={{ fontSize: 11 }}>
                  invoice cards {r.receivesInvoiceCards ? 'on' : 'off'}
                </span>
                {disabled && r.userRole === 'property_manager' && (
                  <span className="field-help" style={{ marginLeft: 'auto' }}>
                    bind + confirm Telegram first
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GroupAccessChecks({
  binding,
  memberships,
  savingKey,
  onSetMembership,
}: {
  binding: AdminPmBindingPublic;
  memberships: AdminPmGroupMembershipPublic[];
  savingKey: string | null;
  onSetMembership: (
    tenantPmUserId: string,
    groupKind: PmGroupKind,
    enabled: boolean,
  ) => Promise<void>;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
      {(['leasing', 'admin_accounting', 'maintenance', 'etransfers'] as const).map((kind) => {
        const checked = memberships.some(
          (m) => m.tenantPmUserId === binding.tenantPmUserId && m.groupKind === kind,
        );
        const key = `${binding.tenantPmUserId}:${kind}`;
        return (
          <label
            key={kind}
            className="mono"
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}
            title="Attribution only: the connected Telegram group authorizes access for that lane."
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={savingKey === key}
              onChange={(e) =>
                void onSetMembership(binding.tenantPmUserId, kind, e.currentTarget.checked)
              }
            />
            {groupShortLabel(kind)}
          </label>
        );
      })}
    </div>
  );
}

function groupShortLabel(kind: PmGroupKind): string {
  if (kind === 'leasing') return 'leasing';
  if (kind === 'maintenance') return 'maint.';
  if (kind === 'etransfers') return 'e-transfers';
  return 'admin';
}

function groupLabel(kind: PmGroupKind): string {
  if (kind === 'leasing') return 'Leasing';
  if (kind === 'maintenance') return 'Maintenance';
  if (kind === 'etransfers') return 'E-transfers';
  return 'Admin / Accounting';
}

function roleLabel(role: UserRole): string {
  return role === 'leasing_agent' ? 'leasing agent' : 'property manager';
}

function SetupInviteSection({
  tenantSlug,
  tenantStatus,
  onSessionExpired,
}: {
  tenantSlug: string;
  tenantStatus: ClientStatus;
  onSessionExpired: () => void;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [pmDisplayName, setPmDisplayName] = useState('');
  const [pmPhone, setPmPhone] = useState('');
  const [pmEmail, setPmEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminSetupInviteCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const canSubmit =
    tenantStatus === 'live' &&
    /^\+[1-9]\d{1,14}$/.test(pmPhone.trim()) &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(pmEmail.trim());

  function reset() {
    setFormOpen(false);
    setPmDisplayName('');
    setPmPhone('');
    setPmEmail('');
    setError(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminSetupInviteCreate({
        tenantSlug,
        pmDisplayName: pmDisplayName.trim() || undefined,
        pmPhone: pmPhone.trim(),
        pmEmail: pmEmail.trim(),
      });
      setResult(res);
      reset();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      if (err instanceof ApiError && err.status === 502) {
        setError("Couldn't reach the tenant container — provisioning may not be complete yet.");
      } else if (err instanceof ApiError && err.status === 409) {
        setError('Tenant is not live yet — wait for provisioning to finish.');
      } else if (err instanceof ApiNetworkError) {
        setError("Couldn't reach the server.");
      } else {
        setError('Failed to generate setup invite.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function copyUrl() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked; fall back to manual select.
    }
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-4)',
        }}
      >
        <h2 className="h3" style={{ color: 'var(--ink)' }}>
          setup wizard invite
        </h2>
        {!formOpen && !result && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            disabled={tenantStatus !== 'live'}
            className="btn-secondary"
            title={tenantStatus !== 'live' ? 'tenant must be live first' : ''}
            style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
          >
            + setup invite
          </button>
        )}
      </div>

      {!formOpen && !result && (
        <p className="field-help" style={{ marginTop: 0 }}>
          Generate a one-time URL the PM uses to fill Buildium credentials + email forwarding +
          calendar on <code>{tenantSlug}.acelcortex.ai/setup</code>. Distinct from the Telegram-bind
          invite above.
        </p>
      )}

      {result && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            background: 'var(--paper-subtle, #f7f5f2)',
            borderRadius: 'var(--radius-lg, 12px)',
          }}
        >
          <p style={{ color: 'var(--ink)' }}>
            Send this link to <strong>{result.pmDisplayName ?? result.pmEmail}</strong> (
            {result.pmPhone}). They tap it to complete the /setup wizard.
            {result.expiresAt && <> Expires {new Date(result.expiresAt).toLocaleString()}.</>}
          </p>
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              alignItems: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              wordBreak: 'break-all',
              padding: 'var(--space-3)',
              background: 'var(--paper)',
              border: '1px solid var(--ink-faint, #e6e1da)',
              borderRadius: 'var(--radius-md, 8px)',
            }}
          >
            <span style={{ flex: 1 }}>{result.url}</span>
            <button
              type="button"
              onClick={copyUrl}
              className="btn-secondary"
              style={{ padding: '6px 12px', fontSize: 'var(--fs-small)' }}
            >
              {copied ? '✓ copied' : 'copy'}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setResult(null)}
            className="btn-secondary"
            style={{ alignSelf: 'flex-start', padding: '6px 12px', fontSize: 'var(--fs-small)' }}
          >
            done
          </button>
        </div>
      )}

      {formOpen && !result && (
        <form
          onSubmit={submit}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span className="field-label">display name (optional)</span>
            <input
              type="text"
              value={pmDisplayName}
              disabled={submitting}
              onChange={(e) => setPmDisplayName(e.target.value)}
              // biome-ignore lint/a11y/noAutofocus: form is opened by user clicking + setup invite, autofocus expected
              autoFocus
              maxLength={100}
              className="field-input"
              placeholder="Jane Doe"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span className="field-label">phone (E.164)</span>
            <input
              type="tel"
              value={pmPhone}
              disabled={submitting}
              onChange={(e) => setPmPhone(e.target.value)}
              required
              pattern="^\+[1-9][0-9]{1,14}$"
              className="field-input"
              placeholder="+14155551234"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span className="field-label">email</span>
            <input
              type="email"
              value={pmEmail}
              disabled={submitting}
              onChange={(e) => setPmEmail(e.target.value)}
              required
              className="field-input"
              placeholder="pm@spottedproperties.ca"
            />
          </label>
          {error && <p className="field-error">{error}</p>}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="btn-primary"
              style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
            >
              {submitting ? 'generating…' : 'generate setup invite'}
            </button>
            <button
              type="button"
              onClick={reset}
              className="btn-secondary"
              style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
            >
              cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Generic template upload card. Used for both the N4 and N1 bulk-prepare
 * templates (PR #202 added the N4; the N1 reuses the same UI shape).
 * Pass the form kind + the matching API trio.
 *
 * AdminN1TemplateStatus aliases AdminN4TemplateStatus (the server shape
 * is identical), so the state types still reference the N4 names — they
 * are structurally interchangeable.
 */
function TemplateSection({
  formKind,
  tenantSlug,
  tenantStatus,
  onSessionExpired,
  api,
}: {
  formKind: 'n4' | 'n1';
  tenantSlug: string;
  tenantStatus: ClientStatus;
  onSessionExpired: () => void;
  api: {
    status: (slug: string) => Promise<AdminN4TemplateStatus | AdminN1TemplateStatus>;
    upload: (
      slug: string,
      file: File,
    ) => Promise<AdminN4TemplateUploadResponse | AdminN1TemplateUploadResponse>;
    delete: (slug: string) => Promise<{ status: 'cleared' }>;
  };
}) {
  const [statusLoading, setStatusLoading] = useState(true);
  const [status, setStatus] = useState<AdminN4TemplateStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [validationFailure, setValidationFailure] = useState<Extract<
    AdminN4TemplateUploadResponse,
    { status: 'validation_failed' }
  > | null>(null);
  const [lastUpload, setLastUpload] = useState<Extract<
    AdminN4TemplateUploadResponse,
    { status: 'uploaded' }
  > | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const formLabel = formKind.toUpperCase();
  const helpText =
    formKind === 'n4'
      ? 'Upload the PM-signed, PM-block-filled Form N4 PDF. The bulk N4-prepare workflow fills tenant-side fields (names, address, amounts, dates) on top of this template.'
      : 'Upload the PM-signed Form N1 PDF. The bulk N1-prepare workflow fills tenant-side fields (names, address, new rent, increase amount, effective date) on top of this template.';

  async function loadStatus() {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const s = await api.status(tenantSlug);
      setStatus(s as AdminN4TemplateStatus);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      setStatusError(
        err instanceof ApiNetworkError
          ? "Couldn't reach the server."
          : "Couldn't load template status.",
      );
    } finally {
      setStatusLoading(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadStatus is stable per tenantSlug; onSessionExpired is stable
  useEffect(() => {
    void loadStatus();
  }, [tenantSlug]);

  async function handleFileChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setUploadError(null);
    setValidationFailure(null);
    setLastUpload(null);
    try {
      const res = await api.upload(tenantSlug, file);
      if (res.status === 'uploaded') {
        setLastUpload(res);
        await loadStatus();
      } else {
        setValidationFailure(res);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      if (err instanceof ApiError && err.status === 413) {
        setUploadError('File too large (max 5 MB).');
      } else if (err instanceof ApiError && err.status === 502) {
        setUploadError("Couldn't reach the tenant container.");
      } else if (err instanceof ApiNetworkError) {
        setUploadError("Couldn't reach the server.");
      } else {
        setUploadError('Upload failed.');
      }
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleClear() {
    if (
      !window.confirm(
        `Clear the uploaded ${formLabel} template? The bulk workflow will fall back to the bundled blank.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setUploadError(null);
    try {
      await api.delete(tenantSlug);
      setLastUpload(null);
      setValidationFailure(null);
      await loadStatus();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      setUploadError(
        err instanceof ApiNetworkError ? "Couldn't reach the server." : 'Clear failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  const canInteract = tenantStatus === 'live' && !busy;

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-4)',
        }}
      >
        <h2 className="h3" style={{ color: 'var(--ink)' }}>
          {formKind} template
        </h2>
      </div>

      <p className="field-help" style={{ marginTop: 0, marginBottom: 'var(--space-4)' }}>
        {helpText}
      </p>

      {statusLoading && <p className="field-help">loading template status…</p>}

      {!statusLoading && statusError && (
        <p className="field-error" role="alert">
          {statusError}
        </p>
      )}

      {!statusLoading && !statusError && status && status.status === 'uploaded' && (
        <UploadedSummary status={status} />
      )}

      {!statusLoading && !statusError && status && status.status === 'not_uploaded' && (
        <p className="field-help" style={{ fontStyle: 'italic' }}>
          No template uploaded yet — bulk workflow currently falls back to the bundled blank.
        </p>
      )}

      {lastUpload && (
        <div
          style={{
            marginTop: 'var(--space-4)',
            padding: 'var(--space-4)',
            background: 'var(--paper-subtle, #f7f5f2)',
            borderRadius: 'var(--radius-lg, 12px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          <p style={{ color: 'var(--ink)' }}>
            <span style={{ color: 'var(--green)', marginRight: 4 }}>✓</span>
            Uploaded <strong>{lastUpload.fileName}</strong>
          </p>
          <p
            className="field-help"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
            }}
          >
            sha256 {lastUpload.sha256.slice(0, 12)}… · {lastUpload.fieldCount} fields ·{' '}
            {lastUpload.signatureFlattened ? 'signature flattened' : 'signature still editable'} ·{' '}
            {lastUpload.remainingPmFields.length} PM-block field(s) remaining
          </p>
        </div>
      )}

      {validationFailure && (
        <div
          style={{
            marginTop: 'var(--space-4)',
            padding: 'var(--space-4)',
            border: '1px solid #c84a3b',
            borderRadius: 'var(--radius-lg, 12px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          <p className="field-error" role="alert">
            <strong>Validation failed:</strong> {validationFailure.message}
          </p>
          {validationFailure.missingFields && validationFailure.missingFields.length > 0 && (
            <p
              className="field-help"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--fs-mono-sm)',
                wordBreak: 'break-all',
              }}
            >
              missing: {validationFailure.missingFields.join(', ')}
            </p>
          )}
        </div>
      )}

      {uploadError && (
        <p className="field-error" role="alert" style={{ marginTop: 'var(--space-4)' }}>
          {uploadError}
        </p>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        <input
          ref={fileRef}
          id={`${formKind}-template-file`}
          type="file"
          accept="application/pdf,.pdf"
          onChange={handleFileChange}
          disabled={!canInteract}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="btn-primary"
          disabled={!canInteract}
          onClick={() => fileRef.current?.click()}
          title={tenantStatus !== 'live' ? 'tenant must be live first' : ''}
          style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
        >
          {busy
            ? 'validating…'
            : status?.status === 'uploaded'
              ? 'replace template'
              : 'upload template'}
        </button>
        {status?.status === 'uploaded' && (
          <button
            type="button"
            className="btn-secondary"
            disabled={!canInteract}
            onClick={handleClear}
            style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
          >
            clear
          </button>
        )}
      </div>
    </div>
  );
}

function UploadedSummary({
  status,
}: {
  status: Extract<AdminN4TemplateStatus, { status: 'uploaded' }>;
}) {
  const uploadedLabel = status.uploadedAt
    ? new Date(status.uploadedAt).toLocaleString()
    : 'unknown';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <p style={{ color: 'var(--ink)' }}>
        <span style={{ color: 'var(--green)', marginRight: 4 }}>✓</span>
        <strong>{status.fileName}</strong>
      </p>
      <p
        className="field-help"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
        }}
      >
        sha256 {status.sha256 ? `${status.sha256.slice(0, 12)}…` : 'unknown'} ·{' '}
        {(status.sizeBytes / 1024).toFixed(1)} KB · uploaded {uploadedLabel}
      </p>
    </div>
  );
}

function OperationsSection() {
  // PR1 placeholders — "resend setup invite" wired in PR4f (see
  // SetupInviteSection above); "reset Buildium" + "suspend tenant" still
  // disabled with title attributes naming the future PR.
  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-4)' }}>
        operations
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <button
          type="button"
          disabled
          className="btn-secondary"
          title="lands in PR2 (Buildium card)"
        >
          reset Buildium connect
        </button>
        <button
          type="button"
          disabled
          className="btn-secondary"
          title="suspend flow not yet scoped"
        >
          suspend tenant
        </button>
      </div>
    </div>
  );
}

function statusColor(status: ClientStatus): string {
  switch (status) {
    case 'live':
      return 'var(--green)';
    case 'failed':
      return '#c84a3b';
    case 'configuring':
    case 'bootstrapping':
      return '#d2a04f';
    case 'archived':
      return 'var(--muted)';
  }
}
